import React, { useState } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { useIntl, FormattedMessage, Link, navigate } from "gatsby-plugin-intl"
import { BLOCKS } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"

import {
  Table,
  Tbody,
  Tr,
  Td,
  Heading,
  Box,
  Flex,
  Text,
  Select,
  Badge,
  Divider,
  useColorMode,
  Button,
} from "@chakra-ui/react"
import Gallery from "../components/gallery"
import Layout from "../components/layout"
import SEO from "../components/seo"
import { calculateBuyPrice, calculateLeasePrice } from "../utils/calculatePrice"

function embeddedAsset(node) {
  return (
    <img
      style={{
        maxWidth: "100%",
      }}
      src={node?.data?.target?.fields?.file["en-US"]?.url}
      alt=""
    />
  )
}

const BikeProductContentfulTemplate = ({ data, location }) => {
  const product = data?.contentfulProduct
  if (!product) return null
  const intl = useIntl()
  const { colorMode } = useColorMode()
  const isLight = colorMode === "light"
  const [battery, setBattery] = useState(product?.prices?.prices[0]?.battery)
  const [size, setSize] = useState(product?.framesizes?.framesizes[0]?.size)
  const [color, setColor] = useState(product?.colors?.colors[0]?.color)
  const [dropdownImage, setDropdownImage] = useState(
    product?.colors?.colors[0]?.imageName
  )
  const docContent = product.description?.raw
    ? JSON.parse(product.description?.raw)
    : null
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: node => embeddedAsset(node),
    },
  }
  const framesize = product.framesizes?.framesizes?.find(p => p.size === size)
  const price = product.prices?.prices.find(p => p.battery === battery)?.price

  const handleChangeColor = e => {
    setColor(e.target.value)
    const colorObj = product?.colors?.colors?.find(
      c => c.color === e.target.value
    )
    const image = product.images.find(img => img.title === colorObj?.imageName)
    if (image) setDropdownImage(image.title)
  }

  const orderBike = () => {
    const colorObj = product?.colors?.colors?.find(c => c.color === color)
    const image = product.images.find(img => img.title === colorObj?.imageName)
    navigate("/order", { state: { product, size, color, battery, image } })
  }

  return (
    <Layout location={location} container>
      <SEO title={product.name} />
      <Box fontSize="xl" marginTop="5" marginBottom="10">
        <Link to={`/bikes`}>
          ← <FormattedMessage id="back" />
        </Link>
      </Box>
      <Flex direction={["column", "column", "column", "row"]}>
        <Box m={["5", "5", "10", "20"]} flex="3">
          <Gallery images={product.images} dropdownImage={dropdownImage} />
        </Box>
        <Box p="5" flex="3">
          <Heading marginBottom="5">{product.name}</Heading>
          {product.shortDescription?.shortDescription && (
            <Text marginBottom="10">
              {product.shortDescription?.shortDescription}
            </Text>
          )}

          {battery && (
            <>
              <Text marginBottom="1">
                <FormattedMessage id="battery" />
              </Text>
              <Select
                marginBottom="3"
                value={battery}
                onChange={e => setBattery(e.target.value)}
                w="200px"
              >
                {product.prices?.prices.map(price => (
                  <option key={price.battery} value={price.battery}>
                    {price.battery}
                  </option>
                ))}
              </Select>
            </>
          )}

          {color && (
            <>
              <Text marginBottom="1">
                <FormattedMessage id="color" />
              </Text>
              <Select
                marginBottom="3"
                value={color}
                onChange={handleChangeColor}
                w="200px"
              >
                {product.colors?.colors.map(c => (
                  <option key={c?.color} value={c?.color}>
                    {c?.color}
                  </option>
                ))}
              </Select>
            </>
          )}

          {size && (
            <>
              <Text marginBottom="1">
                <FormattedMessage id="size" />
              </Text>
              <Select
                value={size}
                onChange={e => setSize(e.target.value)}
                w="200px"
              >
                {product.framesizes?.framesizes.map(price => (
                  <option key={price.size} value={price.size}>
                    {price.size}
                  </option>
                ))}
              </Select>
            </>
          )}
          <Heading marginTop="8" marginBottom="2" size="sm">
            <FormattedMessage id="buyingPrice" />
          </Heading>

          {!color ? (
            <Heading
              size="lg"
              marginBottom="8"
              color={isLight ? "gray.600" : "gray.400"}
            >
              {`kr ${price}`}
              <Text
                as="i"
                color={isLight ? "gray.400" : "gray.600"}
                fontWeight="200"
                fontSize="20px"
              >
                {"  "}
                inkl mva
              </Text>
            </Heading>
          ) : (
            <>
              <Text
                as="s"
                color={isLight ? "gray.400" : "gray.600"}
              >{`kr ${price}`}</Text>
              <Heading
                size="lg"
                marginBottom="8"
                color={isLight ? "gray.600" : "gray.400"}
              >
                {`kr ${calculateBuyPrice(price, product?.kjopeRabatt)}`}
                <Text
                  as="i"
                  color={isLight ? "gray.400" : "gray.600"}
                  fontWeight="200"
                  fontSize="20px"
                >
                  {"  "}
                  inkl mva
                </Text>
              </Heading>
              <Heading marginTop="8" marginBottom="2" size="sm">
                <FormattedMessage id="leasingPrice" />
              </Heading>
              <Heading
                size="lg"
                marginBottom="8"
                color={isLight ? "gray.600" : "gray.400"}
              >
                {`kr ${calculateLeasePrice(
                  product.prices?.prices.find(p => p.battery === battery)
                    ?.price,
                  product.leasingRabatt
                )} / ${intl.formatMessage({ id: "monthAbbr" })}`}
                <Text
                  as="i"
                  color={isLight ? "gray.400" : "gray.600"}
                  fontWeight="200"
                  fontSize="20px"
                >
                  {"  "}
                  inkl mva
                </Text>
              </Heading>
            </>
          )}

          {color && (
            <>
              {" "}
              <Button onClick={orderBike}>Bestill</Button>{" "}
              <Divider marginBottom="5" marginTop="5" />
            </>
          )}

          {product.tags && (
            <>
              {product.tags.split(",").map(t => (
                <Badge marginRight="1" key="t">
                  {t}
                </Badge>
              ))}
            </>
          )}
        </Box>
      </Flex>
      <Flex direction={["column", "column", "column", "row"]}>
        <Box m={["5", "5", "10", "20"]} flex="3">
          <Box className="bike-product-description">
            {docContent && (
              <Heading size="md" marginBottom="5">
                <FormattedMessage id="description" />
              </Heading>
            )}
            {documentToReactComponents(docContent, options)}
          </Box>
        </Box>
        <Box flex="3">
          <Table>
            <Tbody>
              {framesize && (
                <>
                  <Tr>
                    <Td>
                      <strong>
                        <FormattedMessage id="suspension" />
                      </strong>
                    </Td>
                    <Td>{framesize.suspension}</Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <strong>
                        <FormattedMessage id="tires" />
                      </strong>
                    </Td>
                    <Td>{framesize.tires}</Td>
                  </Tr>
                </>
              )}
              {/* <Tr>
                <Td>
                  <strong>På lager</strong>
                </Td>
                <Td>{String(product.inStock ? "Ja" : "Nei")}</Td>
              </Tr> */}
              {product.engine && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="motor" />
                    </strong>
                  </Td>
                  <Td>{product.engine}</Td>
                </Tr>
              )}
              {product.price && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="price" />
                    </strong>
                  </Td>
                  <Td>{product.price}</Td>
                </Tr>
              )}
              {product.modus && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="modus" />
                    </strong>
                  </Td>
                  <Td>{product.modus}</Td>
                </Tr>
              )}
              {product.lock && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="lock" />
                    </strong>
                  </Td>
                  <Td>{product.lock}</Td>
                </Tr>
              )}

              {product.display && (
                <Tr>
                  <Td>
                    <strong>Display</strong>
                  </Td>
                  <Td>{product.display}</Td>
                </Tr>
              )}
              {product.brakes && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="brakes" />
                    </strong>
                  </Td>
                  <Td>{product.brakes}</Td>
                </Tr>
              )}
              {product.gears && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="gears" />
                    </strong>
                  </Td>
                  <Td>{product.gears}</Td>
                </Tr>
              )}
              {product.batterybrand && (
                <Tr>
                  <Td>
                    <strong>
                      <FormattedMessage id="battery" />
                    </strong>
                  </Td>
                  <Td>{`${product.batterybrand} ${battery}`}</Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Flex>
    </Layout>
  )
}

BikeProductContentfulTemplate.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object,
}

export default BikeProductContentfulTemplate

export const pageQuery = graphql`
  query BikeProductContentfulBySlug($name: String!) {
    contentfulProduct(name: { eq: $name }) {
      images {
        thumb: fluid(maxWidth: 512) {
          ...GatsbyContentfulFluid_noBase64
        }
        full: fluid(maxWidth: 1024) {
          ...GatsbyContentfulFluid_noBase64
        }
        title
      }
      tags
      prices {
        prices {
          price
          battery
        }
      }
      name
      modus
      lights
      inStock
      gears
      engine
      display
      batterybrand
      framesizes {
        framesizes {
          tires
          size
          suspension
        }
      }
      colors {
        colors {
          color
          imageName
        }
      }
      brand
      brakes
      description {
        raw
      }
      shortDescription {
        shortDescription
      }
      leasingRabatt
      kjopeRabatt
    }
  }
`
